import { Icon } from '../Icon';
import { renderBlockCell } from '../BlockCell';
import { suggestBehaviorFromPeer } from '../../lib/peerCalibrationLink';
import type { Member, PeerReportSummary } from '../../types';

interface CalibrationTabProps {
    departmentMembers: Member[];
    departments: string[];
    selectedDeptFilter: string;
    setSelectedDeptFilter: (v: string) => void;
    isLenientWarning: boolean;
    exPercentage: number;
    membersForCalibration: Member[];
    exCount: number;
    totalMembers: number;
    openCalibrationModal: (member: Member) => void;
    peerReportByMember: Record<string, PeerReportSummary>;
}

export function CalibrationTab({
    departmentMembers,
    departments,
    selectedDeptFilter,
    setSelectedDeptFilter,
    isLenientWarning,
    exPercentage,
    membersForCalibration,
    exCount,
    totalMembers,
    openCalibrationModal,
    peerReportByMember,
}: CalibrationTabProps) {
    return (
        <div className="space-y-8">
            <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2.5">
                    <div className="bg-indigo-50 p-2 rounded-xl text-indigo-600">
                        <Icon name="building" className="w-5 h-5" />
                    </div>
                    <div>
                        <h4 className="font-bold text-sm text-slate-900">Calibration 대상 부서 선택</h4>
                        <p className="text-[11px] text-slate-500">부서별 인적 특성 분포에 맞춘 인재 세션 분석 필터링</p>
                    </div>
                </div>
                <div className="w-full sm:w-auto">
                    <select
                        value={selectedDeptFilter}
                        onChange={(e) => setSelectedDeptFilter(e.target.value)}
                        className="w-full sm:w-64 bg-slate-50 border border-slate-300 text-sm font-semibold rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer"
                    >
                        <option value="ALL">전체 구성원 연계 ({departmentMembers.length}명)</option>
                        {departments.map(dept => (
                            <option key={dept} value={dept}>{dept} ({departmentMembers.filter(m => m.department === dept).length}명)</option>
                        ))}
                    </select>
                </div>
            </div>

            {isLenientWarning && (
                <div className="bg-rose-50 border-l-4 border-rose-600 p-4 rounded-r-xl flex items-start gap-3 shadow-sm">
                    <div className="text-rose-600 mt-0.5">
                        <Icon name="shield-alert" className="w-5 h-5" />
                    </div>
                    <div>
                        <h4 className="font-bold text-rose-900 text-sm">최상위 등급(EX) 비율 경고 - 가이드라인 준수 위배</h4>
                        <p className="text-xs text-rose-700 mt-1">
                            현재 선택된 부서 내 EX 등급의 비율이 <strong>{exPercentage}%</strong>에 달합니다.
                            국내 및 글로벌 마켓 기준(최대 20% 이내 제한)을 초과하는 관대화 경향이 감지되었습니다.
                            아래 9-Block Matrix 보드에서 부서원 카드를 클릭하여 1차 등급을 조정해 주세요.
                        </p>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 bg-white border border-slate-200 rounded-3xl p-6 shadow-sm">
                    <div className="flex justify-between items-center mb-6">
                        <div>
                            <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                                <Icon name="bar-chart-2" className="w-5 h-5 text-indigo-600" /> 부서 종합인재 9-Block Matrix 보드
                            </h3>
                            <p className="text-xs text-slate-500 mt-1">성과 등급 × 일하는 방식. 카드 클릭 시 다면평가 추천 등급을 Calibration에 반영할 수 있습니다.</p>
                        </div>
                        <div className="text-xs font-semibold bg-slate-100 px-3 py-1.5 rounded-lg border text-slate-700">
                            EX 등급 비율: {exCount}명 / {totalMembers}명 ({exPercentage}%)
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3 bg-slate-100 p-3 rounded-2xl border border-slate-200 min-h-[500px]">
                        {renderBlockCell("EX", "NI", "집중 육성 대상 (일하는 방식 비대칭)", "bg-indigo-50/50 border-indigo-200", membersForCalibration, openCalibrationModal)}
                        {renderBlockCell("EX", "GD", "우수 인재 (일하는 방식 코칭)", "bg-indigo-50/50 border-indigo-200", membersForCalibration, openCalibrationModal)}
                        {renderBlockCell("EX", "EX", "고성과 인재", "bg-slate-900 text-white border-slate-800", membersForCalibration, openCalibrationModal, true)}

                        {renderBlockCell("GD", "NI", "집중 육성 대상 (일하는 방식)", "bg-slate-50 border-slate-200", membersForCalibration, openCalibrationModal)}
                        {renderBlockCell("GD", "GD", "보통 성과자", "bg-slate-50 border-slate-200", membersForCalibration, openCalibrationModal)}
                        {renderBlockCell("GD", "EX", "우수 인재 (성과 코칭)", "bg-indigo-50/50 border-indigo-200", membersForCalibration, openCalibrationModal)}

                        {renderBlockCell("NI", "NI", "한계 인력", "bg-rose-50/60 border-rose-100", membersForCalibration, openCalibrationModal)}
                        {renderBlockCell("NI", "GD", "집중 육성 대상 (성과 창출)", "bg-slate-50 border-slate-200", membersForCalibration, openCalibrationModal)}
                        {renderBlockCell("NI", "EX", "집중 육성 대상 (성과 비대칭)", "bg-rose-50/60 border-rose-100", membersForCalibration, openCalibrationModal)}
                    </div>

                    <div className="flex justify-between items-center mt-4 text-[11px] text-slate-400">
                        <span>◀ 좌측 방향: 일하는 방식 보완 필요 (NI)</span>
                        <span>우측 방향: 일하는 방식 매우 우수 (EX) ▶</span>
                    </div>
                </div>

                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-slate-900 text-white rounded-3xl p-5 border border-slate-800 shadow-xl">
                        <h4 className="font-bold text-sm mb-3 flex items-center gap-2">
                            <Icon name="shield" className="w-4 h-4 text-indigo-400" /> Calibration 점검 포인트
                        </h4>
                        <p className="text-xs text-slate-300 leading-relaxed mb-4">
                            본부/사업부 Calibration 세션 진행 시, 평가 오류 차단 및 배분율 연계를 위해 아래 핵심 질문을 토대로 구성원을 검증하십시오.
                        </p>

                        <div className="space-y-4 text-xs">
                            <div className="bg-slate-800/80 p-3.5 rounded-xl border border-slate-700/50">
                                <h5 className="font-bold text-indigo-300 mb-1 flex items-center gap-1">
                                    <span className="bg-indigo-950 text-indigo-300 w-4 h-4 text-[9px] font-extrabold flex items-center justify-center rounded-full">1</span>
                                    최상위 EX등급 논의 기준
                                </h5>
                                <p className="text-slate-400 leading-relaxed mt-1">
                                    평균 등급인 GD 부서원 대비 EX를 부여받은 대상자만이 가진 "명확하고 탁월한 절대적 기여 성과 근거"를 리더들에게 당당히 피력할 수 있는가?
                                </p>
                            </div>

                            <div className="bg-slate-800/80 p-3.5 rounded-xl border border-slate-700/50">
                                <h5 className="font-bold text-rose-300 mb-1 flex items-center gap-1">
                                    <span className="bg-rose-950 text-rose-300 w-4 h-4 text-[9px] font-extrabold flex items-center justify-center rounded-full">2</span>
                                    최하위 NI등급 논의 기준
                                </h5>
                                <p className="text-slate-400 leading-relaxed mt-1">
                                    단순 태도 불량인가, 아니면 직무 미스매칭인가? 작년 대비 성과 변화 추이와 개선을 위한 면담 및 액션플랜이 사전 이행되었는가?
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-3xl p-5 shadow-sm">
                        <h4 className="font-bold text-sm text-slate-900 mb-3 flex items-center gap-2">
                            <Icon name="users" className="w-4 h-4 text-indigo-600" /> 부서원 명단 및 1차 등급
                        </h4>
                        <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                            {membersForCalibration.map(m => {
                                const peerSuggest = suggestBehaviorFromPeer(peerReportByMember[m.id] as never);
                                const diverge = peerSuggest && peerSuggest.grade !== m.finalBehavior;
                                return (
                                <div
                                    key={m.id}
                                    onClick={() => openCalibrationModal(m)}
                                    className="p-3 bg-slate-50 border border-slate-200 rounded-xl hover:border-indigo-400 hover:bg-indigo-50/20 cursor-pointer transition-all flex justify-between items-center gap-2"
                                >
                                    <div className="min-w-0">
                                        <span className="font-bold text-slate-900 text-xs">{m.name} {m.duty}</span>
                                        <p className="text-[10px] text-slate-400 mt-0.5 truncate max-w-[150px]">{m.note || '설명 없음'}</p>
                                        {peerSuggest && (
                                            <p className={`text-[10px] mt-1 font-semibold ${diverge ? 'text-violet-700' : 'text-emerald-700'}`}>
                                                다면추천 {peerSuggest.grade}
                                                {diverge ? ' · 현재와 다름' : ' · 일치'}
                                                {' '}({peerSuggest.positivityRate}%)
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex gap-1 text-[9px] font-bold shrink-0">
                                        <span className="bg-slate-200 text-slate-700 px-2 py-0.5 rounded">성과: {m.finalPerf}</span>
                                        <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">방식: {m.finalBehavior}</span>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
